import { useState } from 'react';
import { Plus } from 'lucide-react';
import { patch, post } from './api';
import { useBusiness } from './business';
import { Alert, Button, Card, Field, Input, Select } from './ui';
import { attempt, useLoad } from './useLoad';

interface Promotion {
  id: string; name: string; kind: 'buy_x_pay_y' | 'percent'; buyQty: number | null; payQty: number | null; percent: string | null;
  productIds: string[] | null; categoryIds: string[] | null; collections?: string[] | null; startsOn: string | null; endsOn: string | null; weekdays: number[] | null;
  timeFrom: string | null; timeTo: string | null; active: boolean;
}
interface Named { id: string; name: string; active: boolean; collection?: string | null }

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const EMPTY = { name: '', kind: 'buy_x_pay_y' as Promotion['kind'], buyQty: '3', payQty: '2', percent: '10', scope: 'products' as 'all' | 'products' | 'categories' | 'collections', productIds: [] as string[], categoryIds: [] as string[], collections: [] as string[], startsOn: '', endsOn: '', weekdays: [] as number[], timeFrom: '', timeTo: '' };
const dmy = (d: string | null) => (d ? d.split('-').reverse().join('/') : null);

/** Promoções: criadas aqui, aplicadas sozinhas nas caixas (sem PIN de gerente). */
export function PromotionsPage() {
  const list = useLoad<{ items: Promotion[] }>('/promotions');
  const prods = useLoad<{ items: Named[] }>('/products');
  const cats = useLoad<{ items: Named[] }>('/categories');
  const [f, setF] = useState<typeof EMPTY | null>(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const { has } = useBusiness();
  if (list.error) return <Alert>{list.error}</Alert>;
  if (!list.data || !prods.data || !cats.data) return <p className="text-muted">A carregar…</p>;
  const pname = new Map(prods.data.items.map((p) => [p.id, p.name]));
  const cname = new Map(cats.data.items.map((c) => [c.id, c.name]));
  const collections = [...new Set(prods.data.items.map((p) => p.collection).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b, 'pt'));

  const save = async () => {
    if (!f) return;
    const pct = f.kind === 'percent';
    const e = await attempt(() => post('/promotions', {
      name: f.name, kind: f.kind,
      ...(pct ? { percent: f.percent.replace(',', '.') } : { buyQty: Number(f.buyQty), payQty: Number(f.payQty) }),
      productIds: !pct || f.scope === 'products' ? f.productIds : null,
      categoryIds: pct && f.scope === 'categories' ? f.categoryIds : null,
      collections: pct && f.scope === 'collections' ? f.collections : null,
      startsOn: f.startsOn || null, endsOn: f.endsOn || null, weekdays: f.weekdays.length ? f.weekdays : null,
      timeFrom: f.timeFrom || null, timeTo: f.timeTo || null,
    }));
    setErr(e);
    if (!e) { setF(null); void list.reload(); }
  };
  const toggle = (arr: string[], id: string) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  return (
    <div className="max-w-4xl space-y-4">
      <Alert tone="info">As promoções aplicam-se sozinhas nas caixas, sem PIN de gerente, e não acumulam com outros descontos. Depois de criada, uma promoção só muda de nome, de data de fim ou de estado: para mudar as condições, termine-a e crie outra.</Alert>
      {err && <Alert>{err}</Alert>}
      {f && (
        <Card title="Nova promoção">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nome (aparece no talão)"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Leve 3 pague 2" /></Field>
            <Field label="Tipo">
              <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Promotion['kind'], scope: e.target.value === 'percent' ? f.scope : 'products' })}>
                <option value="buy_x_pay_y">Leve X, pague Y</option><option value="percent">Desconto em %</option>
              </Select>
            </Field>
            {f.kind === 'buy_x_pay_y' ? (
              <div className="flex items-end gap-2">
                <Field label="Leve"><Input inputMode="numeric" value={f.buyQty} onChange={(e) => setF({ ...f, buyQty: e.target.value })} className="w-20" /></Field>
                <Field label="Pague"><Input inputMode="numeric" value={f.payQty} onChange={(e) => setF({ ...f, payQty: e.target.value })} className="w-20" /></Field>
              </div>
            ) : (
              <>
                <Field label="Desconto (%)"><Input inputMode="decimal" value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value })} /></Field>
                <Field label="Em">
                  <Select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value as typeof f.scope })}>
                    <option value="products">Artigos escolhidos</option><option value="categories">Categorias</option>
                    {has('collections') && <option value="collections">Colecções (saldos)</option>}<option value="all">Toda a loja</option>
                  </Select>
                </Field>
              </>
            )}
            <Field label="De (opcional)"><Input type="date" value={f.startsOn} onChange={(e) => setF({ ...f, startsOn: e.target.value })} /></Field>
            <Field label="Até (opcional)"><Input type="date" value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} /></Field>
            <Field label="Das (hora feliz, opcional)"><Input type="time" value={f.timeFrom} onChange={(e) => setF({ ...f, timeFrom: e.target.value })} /></Field>
            <Field label="Às"><Input type="time" value={f.timeTo} onChange={(e) => setF({ ...f, timeTo: e.target.value })} /></Field>
          </div>
          <div className="mt-3">
            <div className="text-sm font-medium">Dias da semana <span className="font-normal text-muted">(nenhum = todos)</span></div>
            <div className="mt-1 flex flex-wrap gap-2">
              {DAYS.map((d, i) => (
                <button key={d} type="button" aria-pressed={f.weekdays.includes(i)} onClick={() => setF({ ...f, weekdays: f.weekdays.includes(i) ? f.weekdays.filter((x) => x !== i) : [...f.weekdays, i] })}
                  className={`h-10 rounded-lg border px-3 ${f.weekdays.includes(i) ? 'border-brand-700 bg-brand-700 text-white' : 'border-base'}`}>{d}</button>
              ))}
            </div>
          </div>
          {f.scope === 'products' && (
            <div className="mt-3">
              <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium">Artigos ({f.productIds.length})</span>
                <Input aria-label="Procurar artigos" placeholder="Procurar" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" /></div>
              <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-base p-2">
                {prods.data.items.filter((p) => p.active && (!q || p.name.toLowerCase().includes(q.toLowerCase()))).map((p) => (
                  <label key={p.id} className="flex h-9 items-center gap-2"><input type="checkbox" checked={f.productIds.includes(p.id)} onChange={() => setF({ ...f, productIds: toggle(f.productIds, p.id) })} /> {p.name}</label>
                ))}
              </div>
            </div>
          )}
          {f.kind === 'percent' && f.scope === 'categories' && (
            <div className="mt-3 flex flex-wrap gap-3">
              {cats.data.items.filter((c) => c.active).map((c) => (
                <label key={c.id} className="flex h-9 items-center gap-2"><input type="checkbox" checked={f.categoryIds.includes(c.id)} onChange={() => setF({ ...f, categoryIds: toggle(f.categoryIds, c.id) })} /> {c.name}</label>
              ))}
            </div>
          )}
          {f.kind === 'percent' && f.scope === 'collections' && (
            <div className="mt-3 flex flex-wrap gap-3">
              {!collections.length && <p className="text-sm text-muted">Ainda não há artigos com colecção: indique-a na ficha do artigo.</p>}
              {collections.map((c) => (
                <label key={c} className="flex h-9 items-center gap-2"><input type="checkbox" checked={f.collections.includes(c)} onChange={() => setF({ ...f, collections: toggle(f.collections, c) })} /> {c}</label>
              ))}
            </div>
          )}
          <div className="mt-4 flex gap-2"><Button onClick={save}>Criar</Button><Button variant="ghost" onClick={() => { setF(null); setErr(null); }}>Cancelar</Button></div>
        </Card>
      )}
      <Card title="Promoções" actions={!f && <Button onClick={() => setF({ ...EMPTY })}><Plus size={16} /> Nova</Button>}>
        {!list.data.items.length && <p className="text-muted">Ainda não há promoções.</p>}
        <ul className="divide-y divide-[var(--border)]">
          {list.data.items.map((p) => (
            <li key={p.id} className={`flex flex-wrap items-center gap-3 py-3 ${p.active ? '' : 'opacity-50'}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{p.name} <span className="ml-1 font-normal text-muted">{p.kind === 'percent' ? `−${p.percent?.replace('.', ',')}%` : `leve ${p.buyQty}, pague ${p.payQty}`}</span></div>
                <div className="text-sm text-muted">
                  {[
                    p.productIds ? p.productIds.map((id) => pname.get(id) ?? '?').join(', ') : p.categoryIds ? `Categorias: ${p.categoryIds.map((id) => cname.get(id) ?? '?').join(', ')}` : p.collections ? `Colecções: ${p.collections.join(', ')}` : 'Toda a loja',
                    p.startsOn || p.endsOn ? `${dmy(p.startsOn) ?? '…'} a ${dmy(p.endsOn) ?? '…'}` : null,
                    p.weekdays ? p.weekdays.map((d) => DAYS[d]).join(', ') : null,
                    p.timeFrom ? `${p.timeFrom}–${p.timeTo}` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <Button variant="ghost" onClick={async () => { setErr(await attempt(() => patch(`/promotions/${p.id}`, { active: !p.active }))); void list.reload(); }}>{p.active ? 'Terminar' : 'Reactivar'}</Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
